import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import type { TicketStatusRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useSlug } from "@/hooks/useSlug";
import { type FormValues, TicketStatusForm } from "@/modules/ticket-statuses/components/TicketStatusForm";
import { ticketStatusMutations } from "@/queries/ticket-statuses/mutations";
import { useTicketStatusQueries } from "@/queries/ticket-statuses/queryOptions";
import { routes } from "@/routes";

export function CreateTicketStatusPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const queries = useTicketStatusQueries();
  const statusesQuery = useQuery(queries.list());
  const createStatus = ticketStatusMutations.useCreateTicketStatus();

  if (statusesQuery.error) {
    return <ErrorPage error={statusesQuery.error} />;
  }

  if (statusesQuery.isFetching) {
    return <FullSizeLoader withPadding />;
  }

  return (
    <TicketStatusForm
      isEditMode={false}
      isSubmitting={createStatus.isPending}
      ticketStatuses={statusesQuery.data?.items ?? []}
      defaultFormValues={{ isDefault: false }}
      onSubmit={(values) => {
        createStatus.mutate(toRequest(values), {
          onSuccess: () => void navigate(routes.ticketStatuses.list({ slug })),
        });
      }}
    />
  );
}

function toRequest(values: FormValues): TicketStatusRequest {
  return {
    name: values.name,
    nameTranslations: [],
    color: values.color || "",
    isDefault: values.isDefault,
    type: values.type,
  };
}
